import { signInWithRedirect, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getConfig } from 'common/config';

export const isUserUnAuthenticatedException = (error) => {
  if (!error) return false;
  return error?.name === 'UserUnAuthenticatedException' || 
         error?.message?.includes('authenticated') ||
         error?.message?.includes('User needs to be authenticated');
};

export const mapCognitoPayloadToProfile = (payload, accessToken) => {
  return {
    userid: payload.username,
    name: payload.username,
    email: payload.email,
    id: payload.username,
    mobile_number: payload.phone_number,
    token: accessToken,
  };
};

export const getUserProfileFromAmplify = async () => {
  try {
    const session = await fetchAuthSession();

    if (!session.tokens || !session.tokens.idToken || !session.tokens.accessToken) {
      return null;
    }
    const idToken = session.tokens.idToken;
    const payload = idToken.payload;
    const accessToken = session.tokens.accessToken.toString();
    
    return mapCognitoPayloadToProfile(payload, accessToken);
  } catch (error) {
    if (isUserUnAuthenticatedException(error)) {
      throw error;
    }
    console.error("Error getting user profile from Amplify:", error);
    return null;
  }
};

export const handleAuthSuccess = (profile, setUserProfile, setIsAuthenticating) => {
  window.history.replaceState(null, null, window.location.pathname);
  setUserProfile(profile);
  setIsAuthenticating(false);
};

export const handleAuthError = (error, setAuthError, setIsAuthenticating) => {
  const errorMessage = error?.message || error?.data?.message || 'Unknown error';
  setAuthError(`Authentication failed: ${errorMessage}. Please try again.`);
  setIsAuthenticating(false);
};

export const handleOAuthCallback = async (setUserProfile, setIsAuthenticating, setAuthError) => {
  const TIMEOUT_MS = 15000;
  
  let hubListenerCancelToken = null;
  let timeoutId = null;
  let isResolved = false;
  
  const cleanup = () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (hubListenerCancelToken) hubListenerCancelToken();
  };
  
  const resolveAuth = (profile) => {
    if (isResolved) return;
    isResolved = true;
    cleanup();
    handleAuthSuccess(profile, setUserProfile, setIsAuthenticating);
  };
  
  const rejectAuth = (error) => {
    if (isResolved) return;
    isResolved = true;
    cleanup();
    handleAuthError(error, setAuthError, setIsAuthenticating);
    window.history.replaceState(null, null, window.location.pathname);
  };
  
  hubListenerCancelToken = Hub.listen('auth', async ({ payload }) => {
    if (payload.event === 'signedIn') {
      try {
        const profile = await getUserProfileFromAmplify();
        if (profile) resolveAuth(profile);
      } catch (error) {
        rejectAuth(error);
      }
    } else if (payload.event === 'signIn_failure') {
      rejectAuth(payload.data);
    }
  });
  
  timeoutId = setTimeout(() => {
    rejectAuth({ message: 'Token exchange timeout. Please try again.' });
  }, TIMEOUT_MS);
};

export const checkExistingAuth = async () => {
  try {
    const profile = await getUserProfileFromAmplify();
    return profile;
  } catch (error) {
    if (isUserUnAuthenticatedException(error)) {
      return null; // Expected - user not logged in
    }
    throw error; // Unexpected error
  }
};

export const redirectToLogin = async (setAuthError, setIsAuthenticating) => {
  try {
    await signInWithRedirect();
  } catch (error) {
    setAuthError(
      `Failed to redirect to login: ${error.message}. ` +
      `Please verify that the callback URL is configured in Cognito console.`
    );
    setIsAuthenticating(false);
  }
};

// Helper function to clear all storage
async function clearStorage(PROFILE_KEY, SELECTED_CUSTOMER_KEY) {
  localStorage.setItem(PROFILE_KEY, null);
  localStorage.removeItem(SELECTED_CUSTOMER_KEY);
  localStorage.removeItem("amplify-token");
  localStorage.removeItem("amplify-refresh-token");
  localStorage.removeItem("cognito-access-token");
  localStorage.removeItem("cognito-id-token");
  localStorage.removeItem("cognito-refresh-token");
  localStorage.removeItem("cognito-token-expiry");
  
  sessionStorage.clear();
  
  // Clear IndexedDB for Amplify storage
  try {
    if ('indexedDB' in window) {
      await new Promise((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase('amplify-auth-storage');
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
          setTimeout(() => resolve(), 100);
        };
      });
    }
  } catch (error) {
    console.warn('Error clearing IndexedDB:', error);
  }
}

export const clearLocalAuthSession = async (options = {}) => {
  const {
    PROFILE_KEY = "profile",
    SELECTED_CUSTOMER_KEY = "selected_customer",
  } = options;

  await clearStorage(PROFILE_KEY, SELECTED_CUSTOMER_KEY);
};

export const buildCognitoHostedLogoutUrl = (logoutUri) => {
  const domain = String(getConfig("REACT_APP_COGNITO_DOMAIN") ?? "")
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
  const clientId = String(getConfig("REACT_APP_COGNITO_CLIENT_ID") ?? "").trim();

  if (!domain || !clientId) {
    throw new Error("Missing Cognito domain or client id for hosted logout.");
  }

  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: logoutUri,
  });

  return `https://${domain}/logout?${params.toString()}`;
};

export const signOutForPublicSignup = async (redirectUrl) => {
  const trimmed = String(redirectUrl ?? "").trim();

  try {
    await signOut();
  } catch (error) {
    console.warn("⚠️ [signOutForPublicSignup] Amplify signOut:", error);
  }

  await clearLocalAuthSession();

  if (trimmed) {
    window.location.assign(trimmed);
    return { redirected: true };
  }

  return { redirected: false };
};

export const performLogout = async (options = {}) => {
  const {
    PROFILE_KEY = "profile",
    SELECTED_CUSTOMER_KEY = "selected_customer",
    onBeforeRedirect = null,
    oauthRedirectUrl = null,
  } = options;

  if (onBeforeRedirect) {
    onBeforeRedirect();
  }

  const trimmedRedirect = String(oauthRedirectUrl ?? "").trim();

  if (trimmedRedirect) {
    try {
      await signOut({
        global: false,
        oauth: { redirectUrl: trimmedRedirect },
      });
      return { redirected: true };
    } catch (error) {
      console.warn(
        "⚠️ [performLogout] OAuth sign-out redirect failed (clearing local session):",
        error
      );
    }
  } else {
    try {
      await signOut();
    } catch (error) {
      console.warn("⚠️ [performLogout] signOut had an issue (continuing anyway):", error);
    }
  }

  await clearStorage(PROFILE_KEY, SELECTED_CUSTOMER_KEY);
  return { redirected: false };
};


